import { autoinject, transient } from "aurelia-framework";
import { validateTrigger, ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../client-validation-system/custom-validation-renderer";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { SalesforceCompanyTypes } from "../../../../constants/salesforce/salesforce-company-types";
import { SalesforceCountries } from "../../../../constants/salesforce/salesforce-countries";
import { CrewWelfareSearchToolService } from "../../../../services/vetting/cwsa-search-tool.service";
import { SubmitCWSAClientSearchToolRequest } from "../../../../models/crew-welfare/cwsa-search-tool.models";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { EmailValidator } from "../../../../utilities/email-validator";

@autoinject()
@transient()
export class RequestCrewWelfareSearchTool {

    public selectedCompany: any;
    public firstName: string;
    public lastName: string;
    public companyName: string;
    public jobTitle: string;
    public companyEmailAddress: string;
    public currentStep: "input-form" | "form-submitted" = "input-form";
    public marketing: boolean;
    public terms: boolean;
    public selectedCountry: { isoCode: string, country: string };
    public selectedCompanyType: { id: number, name: string };
    public readonly companyTypes: { id: number, name: string }[] = SalesforceCompanyTypes;
    public readonly countries: { isoCode: string, country: string }[] = SalesforceCountries;
    
    protected tokenInvalidRedirected: boolean = false;
    protected hasAccessibilityCookie: boolean = false;

    constructor(public validationsClientService: ValidationsClientService,
        public crewWelfareService: CrewWelfareSearchToolService,
        private notificationService: NotificationClientService,
        private router: Router,
        private validationController: ValidationController,) {
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.validationController.changeTrigger(validateTrigger.manual);
    }

    async activate(params) {
        if (!params) {
            return;
        }
        if (params.referrer && params.referrer.includes("self-assessment")) {
            this.tokenInvalidRedirected = true;
        }
        const cookies = document.cookie;
        const cookieName = 'accessibility-cwsa-search-tool'
        const parts = cookies.split((`; ${cookieName}=`));
        if (parts.length == 2)  {
            this.hasAccessibilityCookie = true;
            const token = parts.pop().split(';').shift()
            this.router.navigateToRoute(RightShipQiRouterConstants.CrewWelfareSearchToolRouteNames.SelfAssessment, { id: token }, { replace: true, trigger: true });
        } else {
            this.hasAccessibilityCookie = false;
        }
    }

    attached() {
        this.setValidations();
    }

    setValidations() {
            ValidationRules
            .ensure<RequestCrewWelfareSearchTool, any>(x => x.firstName)
            .required()
            .withMessage("First name is required")
            
            .ensure(x => x.lastName)
            .required()
            .withMessage("Last name is required")

            .ensure(x => x.companyName)
            .required()
            .withMessage("Company name is required")
            
            .ensure(x => x.selectedCompanyType)
            .required()
            .withMessage("Company type is required")

            .ensure(x => x.selectedCountry)
            .required()
            .withMessage("Country is required")

            .ensure(x => x.companyEmailAddress)
            .required()
            .satisfies(x => EmailValidator.isValidEmail(x))
            .withMessage("Your company email address is required")

            .ensure(x => x.terms)
            .satisfies(x => x == true)
            .withMessage("Agreeing to terms is required")

            .on(this);

        this.validationController.addObject(this);
    }

    openNewTab(url: string, $event) {
        $event.preventDefault();
        $event.stopPropagation();
        const win = window.open(url, '_blank');
        win.focus();
    }

    async next() {
        if (this.currentStep == "input-form") {
            try {
                const validationResult = await this.validationController.validate();
                if (validationResult.valid) {
                    const request: SubmitCWSAClientSearchToolRequest = {
                        firstName: this.firstName,
                        lastName: this.lastName,
                        companyName: this.companyName,
                        companyType: this.selectedCompanyType.name,
                        country: this.selectedCountry.country,
                        jobTitle: this.jobTitle,
                        companyEmailAddress: this.companyEmailAddress,
                        agreeToReceivedMarketingCommunications: this.marketing,
                        agreeToTermsAndConditions: this.terms
                    }
                    this.crewWelfareService.submitCWSAClientSearchTool(request).then(x => {
                        if (x.validationResult && x.validationResult.hasError) {
                            this.validationsClientService.showValidationErrors(x.validationResult);
                        } else {
                            this.currentStep = "form-submitted";
                        }
                    })
                }
            }
            catch { 
                this.notificationService.show("Error when submitting form", "", NotificationType.Error, 5000);
            }
        }
    }
}